import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  TextField,
  CircularProgress,
  Collapse,
  Checkbox,
  FormGroup,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDevice } from '../contexts/DeviceContext';
import { exercisePopupsService } from '../services/exercisePopupsService';
import {
  ExercisePopupsSettings,
  ExercisePopupsSettingsUpdate,
  ExerciseTypeInfo,
  DEFAULT_EXERCISE_POPUPS_SETTINGS,
  INTERVAL_MINUTES_MIN,
  INTERVAL_MINUTES_MAX,
  EXERCISES_PER_POPUP_MIN,
  EXERCISES_PER_POPUP_MAX,
  resolveTypeTitle,
} from '../types/exercisePopups';

interface Props {
  /** Shared with the statistics section so both label types the same way */
  exerciseTypes: ExerciseTypeInfo[];
  typesLoading: boolean;
}

/**
 * Settings card: random exercises popping up over whatever the child is doing on the
 * tablet. Backed by devices/{id}/config/exercisePopups (contracts/firebase-schema.md §2.5).
 */
const ExercisePopupsSection: React.FC<Props> = ({ exerciseTypes, typesLoading }) => {
  const { t, i18n } = useTranslation();
  const { selectedDevice, showSnackbar } = useDevice();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<ExercisePopupsSettings>(DEFAULT_EXERCISE_POPUPS_SETTINGS);
  const [intervalInput, setIntervalInput] = useState<string>('30');
  const [countInput, setCountInput] = useState<string>('1');

  const isViewer = selectedDevice?.role === 'viewer';

  const loadSettings = useCallback(async () => {
    if (!selectedDevice?.id) return;
    try {
      setLoading(true);
      const data = await exercisePopupsService.getSettings(selectedDevice.id);
      setSettings(data);
      setIntervalInput(String(data.intervalMinutes));
      setCountInput(String(data.exercisesPerPopup));
    } catch (error) {
      console.error('Failed to load exercise popup settings:', error);
      showSnackbar(t('exercisePopups.failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedDevice?.id, showSnackbar, t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSettings = useCallback(
    async (updates: ExercisePopupsSettingsUpdate) => {
      if (!selectedDevice?.id || isViewer) return;
      try {
        setSaving(true);
        const updated = await exercisePopupsService.updateSettings(selectedDevice.id, updates);
        setSettings(updated);
        setIntervalInput(String(updated.intervalMinutes));
        setCountInput(String(updated.exercisesPerPopup));
        showSnackbar(t('exercisePopups.settingsSaved'), 'success');
      } catch (error) {
        console.error('Failed to save exercise popup settings:', error);
        showSnackbar(t('exercisePopups.failedToSave'), 'error');
        loadSettings();
      } finally {
        setSaving(false);
      }
    },
    [selectedDevice?.id, isViewer, showSnackbar, t, loadSettings]
  );

  const handleEnabledChange = (enabled: boolean) => {
    setSettings((prev) => ({ ...prev, enabled }));
    saveSettings({ enabled });
  };

  // Numbers are edited locally and saved on blur to avoid a request per keystroke
  const commitNumber = (
    raw: string,
    min: number,
    max: number,
    current: number,
    reset: (v: string) => void,
    build: (v: number) => ExercisePopupsSettingsUpdate
  ) => {
    const parsed = parseInt(raw, 10);
    if (isNaN(parsed) || parsed < min || parsed > max) {
      reset(String(current));
      return;
    }
    if (parsed !== current) {
      saveSettings(build(parsed));
    }
  };

  const handleTypeToggle = (typeId: string, checked: boolean) => {
    const next = checked
      ? [...settings.exerciseTypes, typeId]
      : settings.exerciseTypes.filter((id) => id !== typeId);
    setSettings((prev) => ({ ...prev, exerciseTypes: next }));
    saveSettings({ exerciseTypes: next });
  };

  // Types that were chosen earlier but are no longer in the catalog (retired)
  const unknownSelected = settings.exerciseTypes.filter(
    (id) => !exerciseTypes.some((type) => type.id === id)
  );
  const allTypesMode = settings.exerciseTypes.length === 0;

  if (!selectedDevice) return null;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('exercisePopups.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('exercisePopups.description')}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enabled}
                  onChange={(e) => handleEnabledChange(e.target.checked)}
                  disabled={isViewer || saving}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">{t('exercisePopups.enable')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('exercisePopups.enableDescription')}
                  </Typography>
                </Box>
              }
            />

            <Collapse in={settings.enabled}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    type="number"
                    label={t('exercisePopups.interval')}
                    value={intervalInput}
                    onChange={(e) => setIntervalInput(e.target.value)}
                    onBlur={() =>
                      commitNumber(
                        intervalInput,
                        INTERVAL_MINUTES_MIN,
                        INTERVAL_MINUTES_MAX,
                        settings.intervalMinutes,
                        setIntervalInput,
                        (v) => ({ intervalMinutes: v })
                      )
                    }
                    disabled={isViewer || saving}
                    inputProps={{ min: INTERVAL_MINUTES_MIN, max: INTERVAL_MINUTES_MAX }}
                    helperText={t('exercisePopups.intervalHelp')}
                    sx={{ flex: '1 1 240px' }}
                  />
                  <TextField
                    type="number"
                    label={t('exercisePopups.count')}
                    value={countInput}
                    onChange={(e) => setCountInput(e.target.value)}
                    onBlur={() =>
                      commitNumber(
                        countInput,
                        EXERCISES_PER_POPUP_MIN,
                        EXERCISES_PER_POPUP_MAX,
                        settings.exercisesPerPopup,
                        setCountInput,
                        (v) => ({ exercisesPerPopup: v })
                      )
                    }
                    disabled={isViewer || saving}
                    inputProps={{ min: EXERCISES_PER_POPUP_MIN, max: EXERCISES_PER_POPUP_MAX }}
                    helperText={t('exercisePopups.countHelp')}
                    sx={{ flex: '1 1 240px' }}
                  />
                </Box>

                <Box>
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    {t('exercisePopups.types')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {allTypesMode
                      ? t('exercisePopups.typesAllHint')
                      : t('exercisePopups.typesSelectedHint', { count: settings.exerciseTypes.length })}
                  </Typography>
                  {typesLoading ? (
                    <CircularProgress size={20} />
                  ) : exerciseTypes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t('exercisePopups.typesEmpty')}
                    </Typography>
                  ) : (
                    <FormGroup>
                      {exerciseTypes.map((type) => (
                        <FormControlLabel
                          key={type.id}
                          control={
                            <Checkbox
                              checked={settings.exerciseTypes.includes(type.id)}
                              onChange={(e) => handleTypeToggle(type.id, e.target.checked)}
                              disabled={isViewer || saving}
                            />
                          }
                          label={resolveTypeTitle(type, i18n.language, type.id)}
                        />
                      ))}
                    </FormGroup>
                  )}
                  {unknownSelected.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      {t('exercisePopups.typesUnknown', { ids: unknownSelected.join(', ') })}
                    </Alert>
                  )}
                </Box>
              </Box>
            </Collapse>

            {saving && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  {t('common.saving')}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ExercisePopupsSection;
